import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Filter } from 'src/assets/dynamicSvg';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuItem } from './MenuItem';
import { CSkeleton, Poppins } from 'src/common';
import { FiltersStructure } from '../types';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { ColorSet } from 'src/redux/reducers/theme';

const Div = styled.div<{ $bgColor: keyof ColorSet }>`
  position: relative;

  .afltr-control {
    height: 52px;
    width: 52px;
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    background-color: ${({ theme, $bgColor }) => theme.colors[$bgColor]};
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    * {
      user-select: none;
    }
    &:hover {
      filter: brightness(1.015);
    }
  }

  .afltr-container {
    position: absolute;
    z-index: 11;
    right: 0px;
    display: flex;
    justify-content: flex-end;
    pointer-events: none;
    width: 200px;

    &__contents {
      width: 268px;
      padding: 25px 20px 20px 20px;
      border: 1px solid ${({ theme }) => theme.colors.stroke};
      background-color: ${({ theme }) => theme.colors.white};
      box-shadow: 0px 0px 37px ${({ theme }) => theme.colors.shadow};
      display: grid;
      grid-gap: 12px;
      pointer-events: all;

      &__divider {
        border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
        margin: 3px 0;
      }
      &__restore {
        &:hover {
          background: ${({ theme }) => theme.colors.gradient};
          -webkit-background-clip: text;
          color: transparent;
          cursor: pointer;
        }
      }
    }
  }
  ${({ theme }) => theme.breakpoints.down('md')} {
    .afltr-control {
      height: 35px;
    }
    .afltr-container {
      left: 15px;
      right: 10px;
      &__contents {
        padding: 20px 25px;
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .afltr-container {
      left: 10px;
      right: 10px;
      &__contents {
        padding: 15px 10px;
      }
    }
  }
`;

export const FiltersLoading = () => {
  const isMd = useBreakpoints('md');

  return <CSkeleton height={isMd ? 35 : 52} width={isMd ? 35 : 52} />;
};

interface OpenFiltersProps<T extends string> {
  setModalType: React.Dispatch<React.SetStateAction<T | null>>;
  handleRestore: () => void;
  content: FiltersStructure<T>[];
  bgColor?: keyof ColorSet;
}

export const OpenFilters = <T extends string>({
  setModalType,
  handleRestore,
  content,
  bgColor = 'cultured',
}: OpenFiltersProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      <Div $bgColor={bgColor} ref={containerRef}>
        <div className="afltr-control" data-cy="filters-control" onClick={() => setIsOpen(!isOpen)}>
          <Filter />
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="afltr-container"
              data-cy="filters-container"
              onClick={(e: any) => e.stopPropagation()}
              initial={{
                opacity: 0,
                scale: 0.99,
                y: -10,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 10,
              }}
              exit={{
                opacity: 0,
                scale: 0.99,
                y: 0,
              }}
              transition={{
                type: 'tween',
                duration: 0.1,
              }}
            >
              <div className="afltr-container__contents">
                {content.map((el) => (
                  <MenuItem
                    key={el.type}
                    title={el.title}
                    onClick={() => {
                      setModalType(el.type);
                      setIsOpen(false);
                    }}
                    icon={el.icon}
                  />
                ))}
                <div className="afltr-container__contents__divider" />
                <Poppins
                  className="afltr-container__contents__restore"
                  px={14}
                  color="cflowerBlue"
                  weight={500}
                  onClick={() => {
                    handleRestore();
                    setIsOpen(false);
                  }}
                >
                  Clear Filters
                </Poppins>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Div>
    </>
  );
};
